import { useRef, useEffect } from 'react'
import gsap from 'gsap'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import FlameParticles from './FlameParticles'
import { ChevronDown } from 'lucide-react'
import { Sangu, Chakram, NamamLogo } from './SacredSymbols'

export default function Hero() {
  const { t } = useTranslation()
  const containerRef = useRef(null)

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.timeline({ defaults: { ease: 'power2.out' } })
        .fromTo('.hero-chakram', { x: -60, opacity: 0 }, { x: 0, opacity: 1, duration: 1 }, 0.2)
        .fromTo('.hero-sangu',   { x: 60, opacity: 0 },  { x: 0, opacity: 1, duration: 1 }, 0.2)
        .fromTo('.hero-label',   { y: 30, opacity: 0 }, { y: 0, opacity: 1, duration: 0.7 }, 0.1)
        .fromTo('.hero-title',   { y: 50, opacity: 0 }, { y: 0, opacity: 1, duration: 0.8 }, 0.4)
        .fromTo('.hero-sub',     { opacity: 0 },         { opacity: 1, duration: 0.6 },       0.7)
        .fromTo('.hero-btns',    { scale: 0.9, opacity: 0 }, { scale: 1, opacity: 1, duration: 0.5 }, 0.9)
        .to(   '.hero-scroll',   { y: 10, duration: 0.7, repeat: -1, yoyo: true, ease: 'power1.inOut' }, 1.2)

      // Slow continuous rotation for Chakram
      gsap.to('.hero-chakram', { rotation: 360, duration: 40, repeat: -1, ease: 'none', transformOrigin: '50% 50%' })

      // Gentle float for Sangu
      gsap.to('.hero-sangu', { y: '-=8', duration: 3, repeat: -1, yoyo: true, ease: 'power1.inOut' })
    }, containerRef)
    return () => ctx.revert()
  }, [])

  return (
    <section
      ref={containerRef}
      className="relative h-screen flex items-center justify-center overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #5C1400 0%, #1A0A00 50%, #0D0D0D 100%)' }}
    >
      {/* ── Deity image — blended into dark background ── */}
      <div className="absolute inset-0 z-1 flex items-center justify-center pointer-events-none">
        <img
          src="/galary/deity.png"
          alt=""
          aria-hidden="true"
          style={{
            height: '100%',
            width: 'auto',
            maxWidth: '60%',
            objectFit: 'contain',
            opacity: 0.28,
            maskImage: [
              'radial-gradient(ellipse 55% 90% at 50% 50%,',
              '  rgba(0,0,0,1) 0%,',
              '  rgba(0,0,0,0.7) 40%,',
              '  rgba(0,0,0,0.2) 65%,',
              '  transparent 85%)',
            ].join(''),
            WebkitMaskImage: [
              'radial-gradient(ellipse 55% 90% at 50% 50%,',
              '  rgba(0,0,0,1) 0%,',
              '  rgba(0,0,0,0.7) 40%,',
              '  rgba(0,0,0,0.2) 65%,',
              '  transparent 85%)',
            ].join(''),
            mixBlendMode: 'luminosity',
          }}
        />
      </div>

      {/* ── Soft gold glow behind deity ── */}
      <div
        className="absolute inset-0 z-2 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 45% 70% at 50% 50%, rgba(255,183,77,0.08) 0%, transparent 70%)',
        }}
      />

      {/* Left Chakram (Discus) */}
      <div className="hero-chakram absolute left-4 md:left-10 lg:left-16 top-1/2 -translate-y-1/2 z-10 hidden sm:block opacity-80">
        <Chakram size={180} />
      </div>

      {/* Right Sangu (Conch) */}
      <div className="hero-sangu absolute right-4 md:right-10 lg:right-16 top-1/2 -translate-y-1/2 z-10 hidden sm:block opacity-80">
        <Sangu size={180} />
      </div>

      {/* Content */}
      <div className="relative z-10 text-center px-4 max-w-3xl mx-auto">

        {/* Chip with logo+namam avatar inside */}
        <div className="hero-label flex justify-center mb-3">
          <div
            className="inline-flex items-center gap-3 text-sm md:text-base font-semibold tracking-wide rounded-full pl-1.5 pr-5 py-1.5"
            style={{ color: '#FFB74D', border: '1px solid rgba(255,183,77,0.4)' }}
          >
            <span className="relative inline-flex shrink-0">
              <img
                src="/galary/logo2.jpg"
                alt="Temple Logo"
                className="h-9 w-9 md:h-10 md:w-10 rounded-full object-cover"
                style={{ boxShadow: '0 0 0 2px #FFB74D, 0 0 14px rgba(255,183,77,0.5)' }}
              />
              <span className="absolute -bottom-1 left-1/2 -translate-x-1/2">
                <NamamLogo size={12} />
              </span>
            </span>
            <span>{t('hero.label')}</span>
          </div>
        </div>

        {/* Main heading */}
        <h1
          className="hero-title font-tamil text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-2"
          style={{ color: '#FFFFFF' }}
        >
          {t('hero.title')}
        </h1>

        {/* English subtitle */}
        <p
          className="hero-sub font-display text-base md:text-xl mb-6 tracking-wide"
          style={{ color: '#FFB74D' }}
        >
          {t('hero.subtitle')}
        </p>

        {/* CTA buttons */}
        <div className="hero-btns flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/pooja"
            className="font-semibold px-7 py-3 rounded-full transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 text-sm md:text-base"
            style={{ backgroundColor: '#FF6B00', color: '#fff' }}
          >
            {t('hero.bookPooja')}
          </Link>
          <Link
            to="/pooja"
            className="font-semibold px-7 py-3 rounded-full transition-all text-sm md:text-base"
            style={{ border: '2px solid rgba(255,255,255,0.8)', color: '#fff' }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor='#fff'; e.currentTarget.style.color='#1A1A1A' }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor='transparent'; e.currentTarget.style.color='#fff' }}
          >
            {t('hero.viewTimings')}
          </Link>
        </div>
      </div>

      {/* Scroll indicator — pinned at very bottom, never overlaps buttons */}
      <div
        className="hero-scroll absolute bottom-5 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1"
        style={{ color: 'rgba(255,255,255,0.45)' }}
      >
        <span className="text-[10px] tracking-widest uppercase hidden sm:block">Scroll</span>
        <ChevronDown size={16} />
      </div>

      <FlameParticles />
    </section>
  )
}
